import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { signOut, getCurrentUser } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { ThemeToggle } from '../theme/theme-toggle';
import { APP_VERSION } from '../../version';
import {
  Files,
  Upload,
  Link2,
  Plug,
  Database,
  User,
  LogOut,
  MoreHorizontal,
  Code2,
  Box,
  Shield,
  Sheet,
  Workflow,
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';

interface TopNavProps {
  userEmail?: string;
}

export const TopNav: React.FC<TopNavProps> = ({ userEmail }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState(userEmail || '');
  const { canAccess } = usePermissions();

  React.useEffect(() => {
    if (!userEmail) {
      getCurrentUser().then(user => {
        setEmail(user.signInDetails?.loginId || '');
      }).catch(() => {});
    }
  }, [userEmail]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const allNavItems = [
    { path: '/flows', icon: Workflow, label: 'Flows', feature: 'flows' },
    { path: '/dashboard', icon: Files, label: 'Files', feature: 'dashboard' },
    { path: '/upload', icon: Upload, label: 'Ingest', feature: 'upload' },
    { path: '/sheets', icon: Sheet, label: 'Sheets', feature: 'sheets' },
    { path: '/visualize', icon: Box, label: 'Visualize', feature: 'visualize' },
    { path: '/developer', icon: Code2, label: 'Developer', feature: 'developer-api' },
    { path: '/access-control', icon: Shield, label: 'Access', feature: 'access-control' },
  ];

  const allMoreItems = [
    { path: '/ai-connections', icon: Link2, label: 'AI Connections', feature: 'ai-connections' },
    { path: '/integrations', icon: Plug, label: 'Integrations', feature: 'integrations' },
    { path: '/usage', icon: Database, label: 'Usage', feature: 'usage' },
    { path: '/profile', icon: User, label: 'Profile', feature: 'profile' },
  ];

  const navItems = allNavItems.filter(item =>
    item.feature === 'chat' || item.feature === 'access-control' || canAccess(item.feature)
  );
  const moreItems = allMoreItems.filter(item => canAccess(item.feature));

  return (
    <header className="sticky top-0 z-50 w-full bg-black">
      {/* Row 1: Brand bar */}
      <div className="flex h-10 items-center justify-between px-5 border-b border-neutral-800/60">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src="/bigsplice-logo.png" alt="BigSplice" className="h-6 w-6 object-contain" />
          <span className="font-semibold text-sm text-white tracking-tight">BigSplice</span>
        </Link>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-neutral-600 font-mono">v{APP_VERSION}</span>
          <ThemeToggle />
          {email && (
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-neutral-500">
              <User className="h-3.5 w-3.5" />
              {email}
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={handleLogout} className="h-7 w-7 p-0 text-neutral-500 hover:text-white hover:bg-neutral-800">
            <LogOut className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Row 2: Navigation */}
      <div className="flex h-9 items-center px-5 border-b border-neutral-800">
        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded text-[13px] transition-colors ${
                isActive(item.path)
                  ? 'text-white bg-neutral-800/80'
                  : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800/40'
              }`}
            >
              <item.icon className="h-3.5 w-3.5" />
              {item.label}
            </Link>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={`flex items-center gap-1.5 h-auto px-3 py-1.5 text-[13px] ${
                  moreItems.some(item => isActive(item.path))
                    ? 'text-white bg-neutral-800/80'
                    : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800/40'
                }`}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
                More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {moreItems.map((item) => (
                <DropdownMenuItem key={item.path} asChild>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-2 text-[13px] ${
                      isActive(item.path) ? 'font-medium' : ''
                    }`}
                  >
                    <item.icon className="h-3.5 w-3.5" />
                    {item.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>
    </header>
  );
};
